import { OpsType } from '@weave/schema-gen-ts/dist/shared/organization/ops_type.pb';
import { Merchant } from '@frontend/api-payments';
import { useDebugMode } from '@frontend/debug-web';
import { useMultiLocationUtils, useMerchants } from '@frontend/payments-hooks';

const MIN_WARN = 90;
const MAX_WARN = 117;
const MIN_ERROR = 119;
const MAX_ERROR = 120;

export type PayoutsPausingStatus = 'active' | 'warn' | 'alert' | 'error';

export type PayoutsPausingStatusType = 'active' | 'disabled_pending' | 'disabled' | 'reenabled';

export const getMerchantPayoutsPausingStatus = ({
  daysSinceLastActivity,
  payoutsEnabled,
  merchant,
}: {
  daysSinceLastActivity: number;
  payoutsEnabled: boolean;
  merchant?: Merchant;
}) => {
  let type: PayoutsPausingStatusType = 'active';

  let daysUntilPause: boolean | number = false;

  let status: PayoutsPausingStatus = 'active';
  if (merchant) {
    if (!payoutsEnabled) {
      status = 'error';
    } else if (daysSinceLastActivity >= MIN_WARN && daysSinceLastActivity <= MAX_WARN) {
      status = 'warn';
    } else if (daysSinceLastActivity >= MIN_ERROR && daysSinceLastActivity < MAX_ERROR) {
      status = 'alert';
    } else if (daysSinceLastActivity >= MAX_ERROR) {
      status = 'error';
    }
  }

  if (daysSinceLastActivity <= MAX_ERROR && payoutsEnabled) {
    type = 'disabled_pending';
    daysUntilPause = MAX_ERROR - daysSinceLastActivity;
  } else if (daysSinceLastActivity >= MAX_ERROR || !payoutsEnabled) {
    type = 'disabled';
  } else {
    type = 'reenabled';
  }

  return { status, type, daysUntilPause };
};

export const useMerchantPayoutsPausingStatus = (locationId: string | undefined) => {
  const { daysSinceLastActivity, payoutsEnabled, merchant } = useMerchants(locationId)['firstLocation'];

  return getMerchantPayoutsPausingStatus({ daysSinceLastActivity, payoutsEnabled, merchant });
};

export function useShouldShowPayoutsPausingAlert(locationId: string | undefined): boolean {
  const { allMerchantsLoaded, anyLocationHasPayments } = useMerchants('selectedLocationIds');

  const { getLocationOpsType } = useMultiLocationUtils();
  const opsType = getLocationOpsType(locationId) ?? {};
  const isProdLocation = opsType === OpsType.REGULAR;
  const [isDebugMode] = useDebugMode();

  return (isProdLocation || isDebugMode) && allMerchantsLoaded && anyLocationHasPayments;
}
